import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  FP_SKIP_FOOTER_ATTRIBUTE,
  FP_SKIP_HEADER_ATTRIBUTE,
  FP_SKIP_FIRST_HEADER_ATTRIBUTE,
} from "./documentEditor";

/** The skip attribute and label copy for each repeatable page-chrome kind. */
const CHROME_TARGETS = {
  header: {
    attribute: FP_SKIP_HEADER_ATTRIBUTE,
    label: "Hide header throughout this section",
  },
  firstHeader: {
    attribute: FP_SKIP_FIRST_HEADER_ATTRIBUTE,
    label: "Hide header on this section’s first page only",
  },
  footer: {
    attribute: FP_SKIP_FOOTER_ATTRIBUTE,
    label: "Hide footer throughout this section",
  },
} as const;

interface SkipPageChromeToggleProps {
  /** Which repeatable page chrome the toggle hides. */
  target: keyof typeof CHROME_TARGETS;
  /** Whether the block currently carries the skip attribute. */
  checked: boolean;
  disabled?: boolean;
  /** Writes (or clears, on "") one attribute on the selected block. */
  onAttributeChange: (attribute: string, value: string) => void;
}

/**
 * Hides page chrome throughout a section or its first page only. Only
 * rendered for a block that opens a page region, since the renderer ignores the
 * attribute anywhere else.
 * @param target - Which repeatable page chrome the toggle hides
 * @param checked - Whether the chrome is currently hidden on this page
 * @param disabled - Whether section-wide hiding takes precedence
 * @param onAttributeChange - Writes or clears the attribute
 * @returns The skip toggle
 */
export function SkipPageChromeToggle({
  target,
  checked,
  disabled,
  onAttributeChange,
}: SkipPageChromeToggleProps) {
  const { attribute, label } = CHROME_TARGETS[target];
  return (
    <div className="flex items-center gap-2">
      <Checkbox
        id={`skip-${target}`}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(state) =>
          onAttributeChange(attribute, state === true ? "true" : "")
        }
      />
      <Label htmlFor={`skip-${target}`} className="text-[11px] font-normal">
        {label}
      </Label>
    </div>
  );
}
